function collectAttrs(theObj: any) {
    return Object.keys(theObj).map((key) => {
        const val = theObj[key];
        if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
            const aTuple: any[] = [key, val];
            return aTuple;
        }
        const aTuple: any[] = [key, typeof val];
        return aTuple;
    });
}

function AnnualSummariesQuickDisplay({ plan }: { plan: CarbonReductionPlan }) {
    const asums = plan.annualSummaries;
    asums.sort((a, b) => a.year - b.year);
    const theKeys = Object.keys(asums[0]);
    const theData = theKeys.map((key) => asums.map((asum) => asum[key as keyof AnnualSummary]));
    const theYears = asums.map((a) => a.year);
    return (
        <div>
            <h4>Annual Summaries</h4>
            <table>
                <tr>
                    <th>Attribute</th>
                    {theYears.map((year) => (
                        <th key={year}>{year}</th>
                    ))}
                </tr>
                {theData.map((aRow, i) => (
                    <tr key={theKeys[i]}>
                        <td>{theKeys[i]}</td>
                        {aRow.map((aCell, j) => (
                            <td key={theYears[j]}>{aCell}</td>
                        ))}
                    </tr>
                ))}
            </table>
        </div>
    );
}

function MeasuresQuickDisplay({ plan }: { plan: CarbonReductionPlan }) {
    const measures = plan.carbonReductionMeasures;
    measures.sort((a, b) => a.yearApplied - b.yearApplied);
    const theKeys = Object.keys(measures[0]);
    const theData = theKeys.map((key) =>
        measures.map((measure) => {
            const val = measure[key as keyof CarbonReductionMeasure];
            if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
                return val;
            }
            return "object";
        })
    );
    const theYears = measures.map((a) => a.yearApplied);
    return (
        <div>
            <h4>Carbon Reduction Measures</h4>
            <table>
                <tr>
                    <th>Attribute</th>
                    {theYears.map((year) => (
                        <th key={year}>{year}</th>
                    ))}
                </tr>
                {theData.map((aRow, i) => (
                    <tr key={theKeys[i]}>
                        <td>{theKeys[i]}</td>
                        {aRow.map((aCell, j) => (
                            <td key={theYears[j]}>{aCell}</td>
                        ))}
                    </tr>
                ))}
            </table>
        </div>
    );
}

function PlanQuickDisplay({ bmcrp }: { bmcrp: BuildingModelCarbonReductionPlan }) {
    const decarb = bmcrp.plan;
    const noaction = bmcrp.baselinePlan;
    const planAttrs = collectAttrs(decarb);

    return (
        <div style={{ marginTop: "25px" }}>
            <h2>{bmcrp.name}</h2>
            <div>
                <div>
                    {decarb.carbonReductionPlanUid} / {noaction.carbonReductionPlanUid}
                </div>
                <div>Discount rate: {decarb.carbonReductionPlanOptions.netPresentValueDiscountRate}</div>
                <div>Utility Inflation Rate: {decarb.carbonReductionPlanOptions.utilityInflationRate}</div>
                <div>Emissions Weight: {decarb.carbonReductionPlanOptions.weightEmissionSavings}</div>
                <div>NPV Weight: {decarb.carbonReductionPlanOptions.weightNetPresentValue}</div>
                <div>LL Share Cost: {decarb.carbonReductionPlanOptions.defaultLandlordShareCost}</div>
                <div>LL Share Elec: {decarb.carbonReductionPlanOptions.defaultLandlordShareElectricity}</div>
                <div>LL Share NG: {decarb.carbonReductionPlanOptions.defaultLandlordShareNaturalGas}</div>
                <div>LL Share Steam: {decarb.carbonReductionPlanOptions.defaultLandlordShareSteam}</div>

                <div>
                    <ul>
                        {planAttrs.map((aTuple) => (
                            <li key={aTuple[0]}>
                                <strong>{aTuple[0]}:</strong> {String(aTuple[1])}
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <AnnualSummariesQuickDisplay plan={decarb} />
                </div>
                <div>
                    <MeasuresQuickDisplay plan={decarb} />
                </div>
            </div>
        </div>
    );
}

interface BuildingQuickDisplayProps {
    buildingModel: Building;
}

function BuildingQuickDisplay({ buildingModel }: BuildingQuickDisplayProps) {
    const buildingAttrs = collectAttrs(buildingModel);
    const planNames: any[] = [];
    planNames.push(["baselineCrp", "unk", buildingModel.baselineCrp?.carbonReductionPlanUid]);
    planNames.push(["reportedCrp", "unk", buildingModel.reportedCrp?.carbonReductionPlanUid]);
    planNames.push(["recommendedCrp", "unk", buildingModel.recommendedCrp?.carbonReductionPlanUid]);
    buildingModel.carbonReductionPlans.forEach((bmcrp: BuildingModelCarbonReductionPlan) => {
        const decarb = bmcrp.plan;
        const noaction = bmcrp.baselinePlan;
        const noactionName = `${bmcrp.name}-no action`;
        planNames.push([bmcrp.name, bmcrp.planType, decarb.carbonReductionPlanUid]);
        planNames.push([noactionName, bmcrp.planType, noaction.carbonReductionPlanUid]);
    });

    return (
        <div>
            <div>
                <h4>Attributes</h4>
                <ul>
                    {buildingAttrs.map((aTuple) => (
                        <li key={aTuple[0]}>
                            <strong>{aTuple[0]}:</strong> {String(aTuple[1])}
                        </li>
                    ))}
                </ul>
            </div>
            <div>
                <h4>Plans</h4>
                <ul>
                    {planNames.map((aTuple) => (
                        <li key={aTuple[0]}>
                            <strong>
                                {aTuple[0]} ({aTuple[1]}):
                            </strong>{" "}
                            {String(aTuple[2])}
                        </li>
                    ))}
                </ul>

                {buildingModel.carbonReductionPlans.map((aBmcrp) => (
                    <PlanQuickDisplay key={aBmcrp.id} bmcrp={aBmcrp} />
                ))}
            </div>
        </div>
    );
}

export default BuildingQuickDisplay;
